import { useState, useEffect, useMemo, useRef } from 'react';

export const TABLE_ITEMS_PER_PAGE = 50;

function normalizeOptions(secondArg) {
  if (typeof secondArg === 'number') {
    return { itemsPerPage: secondArg, resetDeps: undefined };
  }
  if (secondArg && typeof secondArg === 'object') {
    return {
      itemsPerPage: secondArg.itemsPerPage ?? TABLE_ITEMS_PER_PAGE,
      resetDeps: secondArg.resetDeps
    };
  }
  return { itemsPerPage: TABLE_ITEMS_PER_PAGE, resetDeps: undefined };
}

/** Stable key so inline resetDeps arrays do not trigger spurious resets. */
export function buildPaginationResetSignature(resetDeps) {
  if (resetDeps == null) return null;
  try {
    return JSON.stringify(resetDeps);
  } catch {
    return String(resetDeps);
  }
}

/**
 * Client-side table pagination — fixed rows per page (inventory standard).
 * Pass resetDeps (filter/search/sort values) to jump back to page 1 when they change.
 */
export function useTablePagination(items, optionsOrItemsPerPage = TABLE_ITEMS_PER_PAGE) {
  const { itemsPerPage, resetDeps } = normalizeOptions(optionsOrItemsPerPage);
  const [currentPage, setCurrentPage] = useState(1);
  const resetSignature = buildPaginationResetSignature(resetDeps);
  const prevResetSignatureRef = useRef(undefined);

  useEffect(() => {
    if (resetSignature == null) return;
    if (prevResetSignatureRef.current === undefined) {
      prevResetSignatureRef.current = resetSignature;
      return;
    }
    if (prevResetSignatureRef.current !== resetSignature) {
      prevResetSignatureRef.current = resetSignature;
      setCurrentPage(1);
    }
  }, [resetSignature]);

  const list = Array.isArray(items) ? items : [];
  const totalItems = list.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage) || 1);
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedItems = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return list.slice(start, start + itemsPerPage);
  }, [list, safePage, itemsPerPage]);

  useEffect(() => {
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  }, [safePage, currentPage]);

  return {
    currentPage: safePage,
    setCurrentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    paginatedItems
  };
}
